import { useEffect, useRef, type RefObject } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useDispatch } from 'react-redux';
import { ForgeButton } from '@tylertech/forge-react';

import { I18n } from 'common/i18n';
import { type BlockComponent, type HeroBlockComponent } from 'types';
import StorytellerUtils from 'lib/StorytellerUtils';
import { EnvironmentFactory } from 'StorytellerEnvironment';
import { useRichTextEditor } from 'editor/hooks/useRichTextEditor';
import { useStoryLayout } from 'editor/hooks/useStoryLayout';
import { updateBlockComponent } from 'store/reducers/StoryReducer';
import { providerChosen, selectAssetForComponent } from 'store/reducers/AssetSelectorReducer';
import { type ReactRendererProps } from '../shared/ComponentRegistry';
import { addNoopenerToBlankLinks } from './add-noopener-to-blank-links';

const DEFAULT_HEIGHT_PX = 300;
const MIN_HEIGHT_PX = 150;

type HeroValue = HeroBlockComponent['value'];

const heroHasImage = (componentData: BlockComponent): boolean => {
  const value = (componentData?.value ?? {}) as Partial<HeroValue>;
  return !isEmpty(value) && !isEmpty(value.url);
};

/**
 * A hero is resizable only once it has an image; an unconfigured hero uses its default height.
 */
export const resizableProps = (componentData: BlockComponent) => {
  return {
    resizeSupported: heroHasImage(componentData),
    resizeOptions: {
      minHeight: MIN_HEIGHT_PX,
    },
    defaultHeight: DEFAULT_HEIGHT_PX,
  };
};

const HeroText = ({
  editorRef,
  editMode,
  html,
}: {
  editorRef: RefObject<HTMLDivElement>;
  editMode: boolean;
  html: string;
}) => {
  if (!editMode) {
    return (
      <div
        data-testid="component-hero-text"
        className="hero-text full-width-content typeset squire-formatted hero-body remove-top-margin"
        dangerouslySetInnerHTML={{ __html: addNoopenerToBlankLinks(html) }}
      />
    );
  }

  /**
   * The editor mounts into this element, so it carries the `component-html` class the edit
   * stylesheets key off, and the blinder that covers the editor while a block is dragged.
   */
  return (
    <div
      ref={editorRef}
      data-testid="component-hero-text"
      className="hero-text full-width-content component-html"
    >
      <div className="component-blinder hidden" />
    </div>
  );
};

/**
 * Sample componentData:
 *  {
 *    type: 'hero',
 *    value: {
 *      documentId: '1234',
 *      url: 'https://bucket-name.s3.amazonaws.com/uploads/random/image.jpg',
 *      crop: {},
 *      html: '<h1>Hero title</h1>',
 *      alt: 'Skyline of a city at dusk'
 *    }
 *  }
 */
export const ComponentHero = ({
  componentData,
  theme,
  blockId,
  componentIndex,
  editMode,
}: ReactRendererProps) => {
  const dispatch = useDispatch();
  const valueRef = useRef<Partial<HeroValue>>({});

  const typeClasses = StorytellerUtils.typeToClassesForComponentType(componentData.type);
  const value = (componentData?.value ?? {}) as Partial<HeroValue>;
  const hasImage = heroHasImage(componentData);
  const html = value.html ?? '';

  // Keep current value accessible in event handlers without stale closures
  valueRef.current = value;

  const layout = useStoryLayout();
  const { ref: editorRef } = useRichTextEditor({
    content: html,
    theme,
    layout,
    extraContentClasses: ['hero-body', 'remove-top-margin'],
  });

  // Dispatch hero text content changes to Redux when the rich text editor fires
  useEffect(() => {
    if (!editMode || !editorRef.current) {
      return;
    }

    const heroTextEl = editorRef.current;
    const handleContentChange = (event: Event) => {
      // Stop propagation so StoryRenderer doesn't treat this as a standalone HTML component
      event.stopPropagation();
      const content = (event as CustomEvent<{ content: string }>).detail.content;
      const newValue = { ...valueRef.current, html: content };
      dispatch(updateBlockComponent({ blockId, componentIndex, type: 'hero', value: newValue }));
    };

    heroTextEl.addEventListener('rich-text-editor::content-change', handleContentChange);
    return () => heroTextEl.removeEventListener('rich-text-editor::content-change', handleContentChange);
  }, [editMode, blockId, componentIndex, dispatch, editorRef]);

  const launchImageSelection = () => {
    dispatch(
      selectAssetForComponent({
        blockId,
        componentIndex,
        componentType: 'hero',
        // Preserve existing hero text when reopening the image selector
        ...(value.html && { existingContent: value }),
      }),
    );
    dispatch(providerChosen({ blockId, componentIndex, provider: 'HERO' }));
  };

  const renderHero = () => {
    if (hasImage) {
      const ariaProps = value.alt
        ? { role: 'img', 'aria-label': value.alt }
        : { 'aria-hidden': true as const };

      return (
        <div
          className="hero"
          data-url={value.url}
          data-crop={JSON.stringify(value.crop ?? {})}
          style={{ backgroundImage: `url(${value.url})` }}
          {...ariaProps}
        >
          <HeroText editorRef={editorRef} editMode={editMode} html={html} />
        </div>
      );
    }

    if (editMode) {
      const env = EnvironmentFactory();

      return (
        <div
          className="hero hero-unconfigured"
          data-url=""
          data-crop={JSON.stringify({})}
          onClick={launchImageSelection}
          data-testid="component-hero-unconfigured"
        >
          <HeroText editorRef={editorRef} editMode={editMode} html={html} />
          <div className="hero-add-controls">
            <img src={env.IMAGES?.COVER_IMAGE_ICON} alt="Add Cover Image" />
            <ForgeButton variant="raised" data-testid="component-hero-set-cover-image">
              {I18n.t('editor.components.hero.set_cover_image')}
            </ForgeButton>
          </div>
        </div>
      );
    }

    return (
      <div className="hero hero-no-image hero-default-height">
        <HeroText editorRef={editorRef} editMode={editMode} html={html} />
      </div>
    );
  };

  return <div className={`component ${typeClasses}`}>{renderHero()}</div>;
};
